<template>
  <div ref="containerRef" class="split-view-container" data-testid="split-view-container">
    <!-- Left pane: takes its natural height and determines the container's height -->
    <div v-if="isMeasured" class="split-view-container__pane split-view-container__pane--left" :style="{ width: leftPercent }">
      <slot name="left" />
    </div>

    <!-- Right column holds both the divider and the right pane. Uses absolute
         positioning internally so the right pane fills the container's full
         height (which is defined by the left pane) without contributing to
         layout. This keeps the right pane capped to the left's height purely
         in CSS — no ResizeObserver needed. -->
    <div v-if="isMeasured" class="split-view-container__right-col" :style="{ width: rightColPercent }">
      <SplitViewDivider
        class="split-view-container__divider"
        :is-dragging="isDragging"
        :left-ratio="leftRatio"
        :min-ratio="minRatio"
        :max-ratio="effectiveMaxRatio"
        @pointer-down="onDividerPointerDown"
        @reset="reset"
        @nudge="onNudge"
      />
      <div class="split-view-container__pane split-view-container__pane--right">
        <slot name="right" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount, onMounted, watch } from 'vue';
import SplitViewDivider from './SplitViewDivider.vue';
import { useSplitView } from '../composables/useSplitView';

interface Props {
  defaultRatio?: number;
  minRatio?: number;
  maxRatio?: number;
  storageKey?: string;
  /** Keyboard nudge step (fraction). Default 0.05 = 5% */
  nudgeStep?: number;
  /** Minimum usable width for the right pane, excluding the draggable divider. */
  minRightPaneWidth?: number;
}

const props = withDefaults(defineProps<Props>(), {
  defaultRatio: 0.5,
  minRatio: 0.25,
  maxRatio: 0.75,
  storageKey: undefined,
  nudgeStep: 0.05,
  minRightPaneWidth: 0,
});

const containerRef = ref<HTMLElement | null>(null);
const containerWidth = ref(0);
const dividerWidth = ref(0);
const isMeasured = ref(false);

const effectiveMaxRatio = computed(() => {
  if (!props.minRightPaneWidth || !containerWidth.value) {
    return props.maxRatio;
  }

  const maxRatioForRightPane = 1 - (props.minRightPaneWidth + dividerWidth.value) / containerWidth.value;
  return Math.max(props.minRatio, Math.min(props.maxRatio, maxRatioForRightPane));
});

const { leftRatio, leftPercent, isDragging, startDrag, reset, setRatio, reapplyPreferredRatio } = useSplitView({
  defaultRatio: props.defaultRatio,
  minRatio: props.minRatio,
  maxRatio: effectiveMaxRatio,
  storageKey: props.storageKey,
});

let resizeObserver: ResizeObserver | undefined;

onMounted(() => {
  if (!containerRef.value) {
    return;
  }

  containerWidth.value = containerRef.value.getBoundingClientRect().width;
  dividerWidth.value = parseFloat(getComputedStyle(containerRef.value).getPropertyValue('--split-divider-width')) || 0;
  reapplyPreferredRatio();
  isMeasured.value = true;
  resizeObserver = new ResizeObserver(entries => {
    containerWidth.value = entries[0]?.contentRect.width ?? 0;
  });
  resizeObserver.observe(containerRef.value);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});

watch(effectiveMaxRatio, () => {
  reapplyPreferredRatio();
});

const rightColPercent = computed(() => `${((1 - leftRatio.value) * 100).toFixed(2)}%`);

const onDividerPointerDown = (event: MouseEvent | TouchEvent) => {
  startDrag(event, containerRef.value);
};

const onNudge = (direction: 'left' | 'right') => {
  const delta = direction === 'left' ? -props.nudgeStep : props.nudgeStep;
  setRatio(leftRatio.value + delta);
};
</script>

<style scoped>
.split-view-container {
  @apply relative flex flex-row items-stretch w-full;
  min-height: 0;
  /* Single source of truth for the divider column width. The right pane and
     SplitViewDivider consume this property in CSS; the resize constraint reads
     the resolved value at mount so it stays aligned with the visual divider. */
  --split-divider-width: 28px;
  /* Isolate internal layout so changes outside this element don't cascade
     into the split view's percentage-based sizing. */
  contain: layout;
}

.split-view-container__pane {
  @apply flex flex-col min-w-0;
}

.split-view-container__pane--left {
  /* Left pane drives the container's natural height. Its content (video)
     has an intrinsic aspect ratio, so this is a stable measurement. */
  flex-shrink: 0;
}

.split-view-container__right-col {
  @apply relative;
  flex-shrink: 0;
  /* Zero height so this column does not contribute to the container's
     natural height. The divider and right pane are absolutely positioned
     inside this column, so they fill the container height (= left pane
     height) via top/bottom anchoring. */
}

.split-view-container__divider {
  @apply absolute top-0 bottom-0;
  left: 0;
}

.split-view-container__pane--right {
  @apply absolute top-0 bottom-0 right-0 overflow-hidden;
  /* Leave space for the divider on the left side of this column. */
  left: var(--split-divider-width);
}
</style>
